import { type AppSettings, type Permission, type SettingsKey } from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '../../../components/ui';
import { useAuth } from '../../../hooks/useAuth';
import { useLookups } from '../../../hooks/useMeta';
import { api } from '../../../lib/api';
import { SETTINGS_FORMS } from '../forms';
import { type FormSpec, SettingsForm } from '../SettingsForm';

/** Same mapping as the API: who may change each group. */
const EDIT_PERMISSION: Record<SettingsKey, Permission> = {
  general: 'settings:manage',
  compliance: 'settings:manage',
  sending: 'settings:manage',
  access: 'settings:manage',
  security: 'settings:manage',
  automation: 'automation:manage',
  nextAction: 'scoring:manage',
  dataQuality: 'scoring:manage',
  duplicates: 'scoring:manage',
  opportunities: 'scoring:manage',
};

export function useSettings() {
  return useQuery({ queryKey: ['settings'], queryFn: ({ signal }) => api.get<Partial<AppSettings>>('/api/settings', { signal }) });
}

export function GroupsSection({ keys }: { keys: readonly SettingsKey[] }) {
  const { can } = useAuth();
  const { meta } = useLookups();
  const { data } = useSettings();
  if (!data) return <Skeleton className="h-64 w-full" />;
  return (
    <>
      {keys.map((key) => {
        const value = data[key];
        if (!value) return null;
        let spec: FormSpec<SettingsKey> = SETTINGS_FORMS[key];
        if (key === 'nextAction') {
          const tierOptions = (meta?.tiers ?? []).map((tier) => ({ value: tier.key, label: tier.label }));
          spec = { ...spec, fields: spec.fields.map((field) => (field.path === 'priorityTierKeys' ? { ...field, options: tierOptions } : field)) };
        }
        // Keyed by the stored value so a successful save (or another admin's change) resets the draft.
        return <SettingsForm key={`${key}:${JSON.stringify(value)}`} spec={spec} value={value} canEdit={can(EDIT_PERMISSION[key])} />;
      })}
    </>
  );
}
